import { create } from 'zustand';

const ORG_PROFILE_KEY = 'acquireos_org_profile';

// Default empty org profile
const DEFAULT_PROFILE = {
  companyName: '',
  website: '',
  logoUrl: '',
  brandColor: '#1B3A6B',
  contactEmail: '',
  phone: '',
  officeAddress: '',
  senderName: '',
  emailSignature: '',
  companyDescription: '',
  communicationTone: 'professional', // professional | friendly | formal
};

const getOrgProfileKey = () => {
  try {
    const sessionRaw = localStorage.getItem('acquireos_session');
    if (sessionRaw) {
      const session = JSON.parse(sessionRaw);
      const email = session?.user?.email;
      if (email) {
        return `acquireos_org_profile_${email.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
      }
    }
  } catch (e) {}
  return ORG_PROFILE_KEY;
};

const saveOrgProfile = (profile) => {
  try {
    const key = getOrgProfileKey();
    localStorage.setItem(key, JSON.stringify(profile));
  } catch (e) {
    console.warn('Could not save org profile:', e);
  }
};

const loadOrgProfile = () => {
  try {
    const key = getOrgProfileKey();
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

const clearOrgProfile = () => {
  try {
    const key = getOrgProfileKey();
    localStorage.removeItem(key);
  } catch (e) {}
};

const useOrganizationStore = create((set, get) => ({
  // State
  profile: DEFAULT_PROFILE,
  isProfileComplete: false,
  isLoaded: false,

  // Load profile from localStorage on app start
  loadProfile: () => {
    // Check if we have a user-specific key
    const sessionRaw = localStorage.getItem('acquireos_session');
    let email = null;
    let user = null;
    if (sessionRaw) {
      try {
        const session = JSON.parse(sessionRaw);
        email = session?.user?.email;
        user = session?.user;
      } catch (e) {}
    }

    if (email) {
      const userKey = `acquireos_org_profile_${email.toLowerCase().replace(/[^a-z0-9]/g, '_')}`;
      const userSaved = localStorage.getItem(userKey);
      if (!userSaved) {
        // If there's no user-specific profile yet, check if there's a profile in the default key 'acquireos_org_profile'
        const defaultSaved = localStorage.getItem(ORG_PROFILE_KEY);
        if (defaultSaved) {
          // Copy the default profile to the user-specific profile!
          localStorage.setItem(userKey, defaultSaved);
          localStorage.removeItem(ORG_PROFILE_KEY);
        }
      }
    }

    let saved = loadOrgProfile();
    
    // Clean up "Hans Capitals" leakage if the logged-in user is not associated with it
    if (saved && saved.companyName === 'Hans Capitals' && user && user.company && user.company !== 'Hans Capitals') {
      saved = null;
    }

    if (saved && saved.companyName) {
      set({
        profile: { ...DEFAULT_PROFILE, ...saved },
        isProfileComplete: true,
        isLoaded: true,
      });
    } else {
      if (user) {
        const initialProfile = {
          ...DEFAULT_PROFILE,
          companyName: user.company || '',
          contactEmail: user.email || '',
          senderName: user.name || '',
        };
        // Auto-save this initial profile
        saveOrgProfile(initialProfile);
        set({
          profile: initialProfile,
          isProfileComplete: Boolean(initialProfile.companyName),
          isLoaded: true,
        });
      } else {
        set({
          profile: DEFAULT_PROFILE,
          isProfileComplete: false,
          isLoaded: true,
        });
      }
    }
  },

  // Save / update the full profile
  saveProfile: (profileData) => {
    const merged = { ...DEFAULT_PROFILE, ...profileData };
    saveOrgProfile(merged);
    set({
      profile: merged,
      isProfileComplete: Boolean(merged.companyName),
    });
  },

  // Update individual fields
  updateField: (field, value) => {
    const current = get().profile;
    const updated = { ...current, [field]: value };
    saveOrgProfile(updated);
    set({
      profile: updated,
      isProfileComplete: Boolean(updated.companyName),
    });
  },

  // Reset org profile (e.g., on logout)
  resetProfile: () => {
    clearOrgProfile();
    set({
      profile: DEFAULT_PROFILE,
      isProfileComplete: false,
    });
  },

  // Helper: get branded email signature block
  getEmailSignature: () => {
    const { profile } = get();
    const lines = [];
    if (profile.senderName) lines.push(profile.senderName);
    if (profile.companyName) lines.push(profile.companyName);
    if (profile.phone) lines.push(profile.phone);
    if (profile.contactEmail) lines.push(profile.contactEmail);
    if (profile.website) lines.push(profile.website);
    if (profile.officeAddress) lines.push(profile.officeAddress);
    return profile.emailSignature || lines.join('\n');
  },

  // Helper: get tone-based intro phrase
  getTonePrefix: () => {
    const { profile } = get();
    switch (profile.communicationTone) {
      case 'friendly':   return 'Hi';
      case 'formal':     return 'Dear';
      case 'professional': default: return 'Hello';
    }
  },
}));

export default useOrganizationStore;
